/**
 * Experiences: response schemas and pure mappers.
 * Endpoints: /api/client/activities, /api/client/activities/:id, /api/client/activities/public/:publicId,
 * /api/client/activities/:id/slots, /api/client/activities/:id/quote, /api/activity-wishlist.
 */
import { addDays, parseISO } from 'date-fns';
import { z } from 'zod';
import { getT, ns, translate, type PlainKeyOf } from '@/i18n';
import { toISODate } from '@/lib/clock';
import { formatDays } from '@/lib/format';
import type {
  EligibilityRequest,
  Experience,
  ExperienceAddon,
  ExperienceDay,
  ExperiencePack,
  ExperiencePickup,
  ExperienceQuestion,
  ExperienceQuote,
  ExperienceRequirements,
  ExperienceSelection,
  ExperienceSlot,
  ExperienceSummary,
  ExperienceVehicleAllocation,
  PaymentMode,
  PriceLine,
  ProviderTickets,
} from '../../domain';
import { assetUrl } from './media';

/* ── Schemas ────────────────────────────────────────────────────────────── */

const n = z.number().nullish();
const s = z.string().nullish();
const b = z.boolean().nullish();

const TierSchema = z.object({ minParticipants: n, maxParticipants: n, pricePerPerson: n }).passthrough();
const AddonSchema = z.object({ name: z.string(), price: n, required: b, description: s }).passthrough();
/* P6: what an option asks at booking (pickup, eligibility, questions, requirements, vehicles, price plans). */
const GeoSchema = z.object({ lat: n, lng: n }).passthrough();
const PickupSchema = z
  .object({
    coverage: s,
    areaGeoJson: z.unknown().nullish(),
    places: z
      .array(z.object({ _id: s, name: z.string(), geo: GeoSchema.nullish(), instructions: s, surchargeMinor: n }).passthrough())
      .nullish(),
    instructions: s,
    confirmationTiming: s,
  })
  .passthrough();
const PlanSchema = z
  .object({
    model: s,
    categories: z.array(z.object({ key: s, ageMin: n, ageMax: n, priceMinor: n }).passthrough()).nullish(),
    compositions: z.array(z.object({ infants: n }).passthrough()).nullish(),
  })
  .passthrough();
const OptionScheduleSchema = z
  .object({
    isActive: b,
    pricePlan: PlanSchema.nullish(),
    timePricePlans: z.array(z.object({ pricePlan: PlanSchema.nullish() }).passthrough()).nullish(),
  })
  .passthrough();
const OptionSchema = z
  .object({
    _id: z.string(),
    name: s,
    title: s,
    description: s,
    pricingMode: s,
    groupPrice: n,
    maxParticipants: n,
    priceTiers: z.array(TierSchema).nullish(),
    childPricePerPerson: n,
    isDefault: b,
    order: n,
    isAvailable: b,
    addons: z.array(AddonSchema).nullish(),
    operationalGroupSize: z.object({ min: n, max: n }).passthrough().nullish(),
    meetingMode: s,
    pickup: PickupSchema.nullish(),
    eligibilityPolicy: z.object({ enabled: b, instructions: s }).passthrough().nullish(),
    bookingQuestions: z
      .array(
        z
          .object({ key: z.string(), label: z.string(), type: s, required: b, choices: z.array(z.string()).nullish() })
          .passthrough(),
      )
      .nullish(),
    requirements: z
      .object({ minAge: n, minHeightCm: n, approvalAboveWeightKg: n, requireAdultCompanion: b })
      .passthrough()
      .nullish(),
    vehicleAllocation: z
      .object({ enabled: b, peoplePerVehicle: n, maxVehicles: n, driverMinAge: n, passengerMinAge: n, instructions: s })
      .passthrough()
      .nullish(),
    schedules: z.array(OptionScheduleSchema).nullish(),
  })
  .passthrough();
type OptionRecord = z.infer<typeof OptionSchema>;
const ImageSchema = z.union([z.string(), z.object({ url: s, alt: s, order: n, isMain: b }).passthrough()]);

export const ActivitySchema = z
  .object({
    _id: z.string(),
    publicId: s,
    title: z.string(),
    city: s,
    citySlug: s,
    slug: s,
    categoryId: z.union([z.string(), z.object({ _id: z.string(), name: s, slug: s }).passthrough()]).nullish(),
    images: z.array(ImageSchema).nullish(),
    durationMinutes: n,
    isMultiDay: b,
    bookingType: s,
    pricingModel: s,
    basePricePerPerson: n,
    basePricePerGroup: n,
    childPricePerPerson: n,
    groupSizeMin: n,
    groupSizeMax: n,
    difficulty: s,
    languages: z.array(z.string()).nullish(),
    cancellationPolicy: s,
    cancellationTerms: z.object({ fullRefundHoursBefore: n, notes: s }).passthrough().nullish(),
    descriptionShort: s,
    descriptionLong: s,
    includes: z.array(z.string()).nullish(),
    excludes: z.array(z.string()).nullish(),
    whatToBring: z.array(z.string()).nullish(),
    meetingPoint: z.object({ address: s, instructionsFr: s }).passthrough().nullish(),
    options: z.array(OptionSchema).nullish(),
    addons: z.array(AddonSchema).nullish(),
    fromPriceMinor: n,
    compareAtFromMinor: n,
    /** The reference option the «dès» price is computed on (`domain/activities/pricing/fromPrice.js`). */
    fromPriceOptionId: s,
    /** One line per bookable option (`domain/activities/publicSummary.js`): its own from-price and basis. */
    optionsSummary: z
      .array(
        z
          .object({ _id: s, fromPriceMinor: n, priceBasis: s, groupPriceMinor: n, groupSizeIncluded: n })
          .passthrough(),
      )
      .nullish(),
    /** Clients' post-trip ratings only; `displayRating` blends in the editorial seeded reviews. */
    ratingStats: z.object({ avg: n, total: n }).passthrough().nullish(),
    displayRating: z.object({ avg: n, total: n }).passthrough().nullish(),
    minAge: n,
    waiverRequired: b,
    waiver: z.object({ mode: s, documentUrl: s, instructions: s }).passthrough().nullish(),
    scheduleTemplate: z
      .object({
        weeklyPattern: z
          .array(z.object({ dayOfWeek: n, times: z.array(z.string()).nullish() }).passthrough())
          .nullish(),
      })
      .passthrough()
      .nullish(),
    status: s,
  })
  .passthrough();
export type ActivityRecord = z.infer<typeof ActivitySchema>;
export const ActivityListSchema = z.array(ActivitySchema);

export const SlotSchema = z
  .object({
    _id: z.string(),
    date: z.string(),
    startTime: s,
    endTime: s,
    status: s,
    optionId: s,
    scheduleId: s,
    capacity: n,
    capacityMax: n,
    participantsBooked: n,
    cutoffAt: s,
  })
  .passthrough();
export const SlotListSchema = z.array(SlotSchema);
export type SlotRecord = z.infer<typeof SlotSchema>;

export const ActivityQuoteSchema = z
  .object({
    total: z.number(),
    totalMinor: n,
    platformFeeMinor: n,
    agencyRevenueMinor: n,
    seats: n,
    optionId: s,
    slotId: s,
    bookable: b,
    blockedBy: s,
    breakdown: z
      .array(
        z
          .object({
            kind: s,
            label: s,
            categoryKey: s,
            quantity: n,
            unitMinor: n,
            amountMinor: z.number(),
            key: s,
            freeCount: n,
          })
          .passthrough(),
      )
      .default([]),
    pickupRequired: b,
    pickupSelection: z
      .object({ covered: b, requiresConfirmation: b, reason: s, placeName: s, surchargeMinor: n })
      .passthrough()
      .nullish(),
  })
  .passthrough();

/* P6: free-entry request (ACT-8/9) and the booking read for the success page (ACT-10). */
export const EligibilitySchema = z
  .object({ _id: z.string(), status: z.string(), reservation: z.unknown().nullish(), reviewNote: s })
  .passthrough();

const TicketsSchema = z
  .object({
    status: s,
    documents: z.array(z.object({ title: s, url: s }).passthrough()).nullish(),
    confirmedStartTime: s,
    instructions: s,
  })
  .passthrough();
/** The extras of `GET /api/client/activity-reservations/:id` the stats row does not carry. */
export const ActivityReservationExtrasSchema = z
  .object({
    _id: z.string(),
    status: s,
    voucher: z.object({ code: s, pin: s, qrToken: s }).passthrough().nullish(),
    providerTickets: TicketsSchema.nullish(),
    activityDetails: z
      .object({ participantCount: z.object({ adults: n, children: n, infants: n }).passthrough().nullish() })
      .passthrough()
      .nullish(),
    activity: z.union([z.string(), z.object({ _id: z.string() }).passthrough()]).nullish(),
  })
  .passthrough();
export type ActivityQuoteRecord = z.infer<typeof ActivityQuoteSchema>;

/* ── Helpers ────────────────────────────────────────────────────────────── */

/**
 * Calendar day of a slot date (`yyyy-MM-dd`). The backend reads `ActivitySlot.date` in UTC
 * (`domain/activities/time.js` `toDateKey`, slots written at UTC midnight by the generator): a date stored at
 * 23:00Z is still that UTC day for the server (its booking, voucher and refund clock), so it is here too.
 */
export const slotDay = (iso: string): string => (/^\d{4}-\d{2}-\d{2}/.test(iso) ? iso.slice(0, 10) : '');

export const isObjectId = (v: string) => /^[a-f\d]{24}$/i.test(v);

type LabelKey = PlainKeyOf<typeof ns.labels>;
const DIFFICULTY: Record<string, LabelKey> = {
  easy: 'difficulty.easy',
  moderate: 'difficulty.moderate',
  medium: 'difficulty.moderate',
  challenging: 'difficulty.challenging',
  hard: 'difficulty.hard',
  difficult: 'difficulty.hard',
};
const CANCELLATION: Record<string, LabelKey> = {
  flexible: 'cancellation.flexible',
  moderate: 'cancellation.moderate',
  strict: 'cancellation.strict',
};
const label = (key: LabelKey) => translate(ns.labels, key);

export function durationLabel(minutes: number | null | undefined, multiDay?: boolean | null): string {
  const m = Math.max(0, Number(minutes) || 0);
  if (!m) return '';
  if (multiDay || m >= 24 * 60) {
    const d = Math.max(1, Math.round(m / (24 * 60)));
    return formatDays(d);
  }
  const t = getT(ns.labels);
  if (m < 60) return t('duration.minutes', { minutes: m });
  const h = Math.floor(m / 60);
  const rest = m % 60;
  return rest
    ? t('duration.hoursMinutes', { hours: h, minutes: String(rest).padStart(2, '0') })
    : t('duration.hours', { hours: h });
}

const optionLabel = (o: z.infer<typeof OptionSchema>) => (o.title || o.name || '').trim();

/** Options in display order (bookable only), the default one first-flagged. */
export function sortedOptions(a: ActivityRecord) {
  return [...(a.options ?? [])]
    .filter((o) => o.isAvailable !== false)
    .sort((x, y) => (Number(x.order) || 0) - (Number(y.order) || 0));
}

/**
 * The option selected first, legacy `defaultOption` (`activityReferencePrice.test.tsx`): the server's reference
 * option (`fromPriceOptionId`, the one the «dès» price is computed on), else the one flagged default, else the first.
 */
export const defaultOption = (a: ActivityRecord) => {
  const opts = sortedOptions(a);
  const ref = a.fromPriceOptionId ? opts.find((o) => o._id === a.fromPriceOptionId) : undefined;
  return ref ?? opts.find((o) => o.isDefault) ?? opts[0];
};

const major = (minor: number | null | undefined): number | null =>
  minor === null || minor === undefined || !Number.isFinite(minor) ? null : minor / 100;

/**
 * What one option row shows, out of the server's `optionsSummary` (legacy `optionPrice`, AC-02): the flat group
 * total for a privatisation (`priceBasis: 'per_group'`, with the people it covers), the per-person from-price
 * otherwise. Null amount when the server prices nothing for it: never 0.
 */
export function optionPrice(
  a: Pick<ActivityRecord, 'optionsSummary'>,
  optionId: string | null | undefined,
): { amount: number | null; basis: 'per_person' | 'per_group'; included: number | null } {
  const summary = (a.optionsSummary ?? []).find((o) => !!optionId && o._id === optionId);
  const basis = summary?.priceBasis === 'per_group' ? 'per_group' : 'per_person';
  const amount = major(basis === 'per_group' ? summary?.groupPriceMinor : summary?.fromPriceMinor);
  const included = Number(summary?.groupSizeIncluded) || null;
  return { amount: amount !== null && amount > 0 ? amount : null, basis, included: basis === 'per_group' ? included : null };
}

const isPerGroup = (a: ActivityRecord) => a.pricingModel === 'per_group';

/**
 * Add-ons the backend actually charges: with options, each option's own catalogue (the activity-level
 * list is ignored by the legacy pricing engine once an option is chosen); without options, the activity's.
 */
export function chargeableAddons(a: ActivityRecord): { name: string; price: number }[] {
  const opts = sortedOptions(a);
  const source = opts.length ? opts.flatMap((o) => o.addons ?? []) : (a.addons ?? []);
  const seen = new Set<string>();
  return source
    .filter((x) => !x.required && !seen.has(x.name) && seen.add(x.name))
    .map((x) => ({ name: x.name, price: Number(x.price) || 0 }));
}

export const addonId = (i: number) => `a${i}`;

/** Per-person price for 1, 2, 3… participants from tiered packs (index = participants − 1). */
export function expandTiers(tiers: z.infer<typeof TierSchema>[] | null | undefined): number[] | undefined {
  const list = (tiers ?? []).filter((t) => Number(t.pricePerPerson) > 0 && Number(t.minParticipants) >= 1);
  if (list.length < 2) return undefined;
  const last = Math.max(...list.map((t) => Number(t.maxParticipants ?? t.minParticipants)));
  const out: number[] = [];
  for (let k = 1; k <= last; k++) {
    const t = list.find(
      (x) => k >= Number(x.minParticipants) && (x.maxParticipants == null || k <= Number(x.maxParticipants)),
    );
    if (!t) break;
    out.push(Number(t.pricePerPerson));
  }
  return out.length ? out : undefined;
}

/**
 * The card's «dès» price and its unit. Per person: the server's `fromPriceMinor` (null when it prices nothing,
 * no local fallback). A per-group activity shows a group total («/ véhicule»): the cheapest flat price of its
 * options (the reference option only when the server names one), else the activity's own group price.
 */
function cardPrice(a: ActivityRecord): { fromPrice: number | null; unit: 'person' | 'vehicle' } {
  const perPerson = { fromPrice: major(a.fromPriceMinor) || null, unit: 'person' as const };
  if (!isPerGroup(a)) return perPerson;
  const opts = sortedOptions(a);
  if (!opts.length) {
    const flat = Number(a.basePricePerGroup) || 0;
    return flat > 0 ? { fromPrice: flat, unit: 'vehicle' } : perPerson;
  }
  const ref = a.fromPriceOptionId ? opts.filter((o) => o._id === a.fromPriceOptionId) : [];
  const groups = (ref.length ? ref : opts)
    .map((o) => optionPrice(a, o._id))
    .filter((p) => p.basis === 'per_group' && p.amount !== null)
    .map((p) => p.amount as number);
  return groups.length ? { fromPrice: Math.min(...groups), unit: 'vehicle' } : perPerson;
}

/** Clients' ratings (`ratingStats`, as the legacy card): none until a client has rated (no seeded reviews). */
function ratingOf(a: ActivityRecord): ExperienceSummary['rating'] {
  const total = Number(a.ratingStats?.total) || 0;
  const avg = Number(a.ratingStats?.avg) || 0;
  return total > 0 && avg > 0 ? { average: Math.round(avg * 100) / 100, count: total } : undefined;
}

function sortedImages(baseUrl: string, a: ActivityRecord) {
  return [...(a.images ?? [])]
    .map((img, i) =>
      typeof img === 'string'
        ? { url: assetUrl(baseUrl, img), alt: '', order: i, main: false }
        : { url: assetUrl(baseUrl, img.url), alt: img.alt ?? '', order: Number(img.order ?? i), main: !!img.isMain },
    )
    .filter((i) => i.url)
    .sort((x, y) => Number(y.main) - Number(x.main) || x.order - y.order);
}

const categoryName = (a: ActivityRecord) =>
  a.categoryId && typeof a.categoryId === 'object' ? (a.categoryId.name ?? '') : '';

/* ── Mappers ────────────────────────────────────────────────────────────── */

export function toExperienceSummary(a: ActivityRecord, baseUrl: string): ExperienceSummary {
  const imgs = sortedImages(baseUrl, a);
  const { fromPrice, unit } = cardPrice(a);
  // The server's struck-through price is computed on the per-person figure: never beside a group total.
  const compareAt = unit === 'person' && a.compareAtFromMinor ? a.compareAtFromMinor / 100 : undefined;
  return {
    id: a._id,
    title: a.title,
    category: categoryName(a),
    city: a.city ?? '',
    image: imgs[0]?.url ?? '',
    imageAlt: imgs[0]?.alt || a.title,
    duration: durationLabel(a.durationMinutes, a.isMultiDay),
    durationMinutes: Number(a.durationMinutes) || 0,
    groupLabel:
      a.bookingType === 'private'
        ? label('group.private')
        : translate(ns.labels, 'group.upTo', { count: Number(a.groupSizeMax) || 1 }),
    difficulty: a.difficulty
      ? (DIFFICULTY[a.difficulty] ? label(DIFFICULTY[a.difficulty] as LabelKey) : a.difficulty)
      : undefined,
    fromPrice,
    compareAt: compareAt && fromPrice !== null && compareAt > fromPrice ? compareAt : undefined,
    priceUnit: unit,
    path: publicPath(a),
    rating: ratingOf(a),
  };
}

/** «Excursions & Désert» → «excursions-desert». */
const slugify = (v: string) =>
  v
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-|-$/g, '');

/** Canonical public URL of the old site (`activityRouteUtils` `/activities/:city/:slug/:publicId`), P6/P10. */
export function publicPath(a: Pick<ActivityRecord, 'publicId' | 'citySlug' | 'city' | 'slug' | 'title'>) {
  if (!a.publicId) return undefined;
  const city = a.citySlug || slugify(a.city ?? '') || 'maroc';
  const slug = a.slug || slugify(a.title) || 'experience';
  return `/activities/${encodeURIComponent(city)}/${encodeURIComponent(slug)}/${encodeURIComponent(a.publicId)}`;
}

/* ── Options (P6): bounds, infants, pickup, eligibility, questions, requirements ── */

/**
 * Participants (adults + children) an option takes, port of the legacy `participantBounds` (AC-05):
 * per group → 1 … max(1, maxParticipants ‖ activity max); no tiers → operational group size, else the
 * activity bounds; tiers → the lowest min (≥ 1) and the highest max, an open-ended tier reaching the activity max.
 */
export function participantBounds(a: ActivityRecord, o: OptionRecord | undefined) {
  const actMin = Math.max(1, Number(a.groupSizeMin) || 1);
  const actMax = Math.max(actMin, Number(a.groupSizeMax) || 99);
  if (!o) return { min: actMin, max: actMax, fixed: actMin === actMax };
  if (o.pricingMode === 'per_group') {
    const max = Math.max(1, Number(o.maxParticipants) || actMax);
    return { min: 1, max, fixed: max === 1 };
  }
  const tiers = o.priceTiers ?? [];
  if (!tiers.length) {
    const min = Math.max(1, Number(o.operationalGroupSize?.min) || actMin);
    const max = Math.max(min, Number(o.operationalGroupSize?.max) || actMax);
    return { min, max, fixed: min === max };
  }
  const min = Math.max(1, Math.min(...tiers.map((t) => Number(t.minParticipants) || 1)));
  const open = tiers.some((t) => t.maxParticipants == null);
  const max = open ? Math.max(min, actMax) : Math.max(min, ...tiers.map((t) => Number(t.maxParticipants) || min));
  return { min, max, fixed: !open && min === max };
}

/** The active price plans of an option (new model); empty for a legacy pack. */
const plansOf = (o: OptionRecord) =>
  (o.schedules ?? [])
    .filter((sc) => sc.isActive !== false)
    .flatMap((sc) => [sc.pricePlan, ...(sc.timePricePlans ?? []).map((t) => t.pricePlan)])
    .filter((p): p is NonNullable<typeof p> => !!p);

/**
 * Infants (free, no seat) are offered when the activity and the option accept under-2s and, on the new price
 * plans, when every plan can price one (`participantsFromLegacy` refuses an infant without an infant band).
 */
function infantsOffered(a: ActivityRecord, o: OptionRecord | undefined): boolean {
  if ((Number(a.minAge) || 0) >= 2 || (Number(o?.requirements?.minAge) || 0) >= 2) return false;
  return (o ? plansOf(o) : []).every((p) => {
    if (p.model === 'composition') return (p.compositions ?? []).some((c) => Number(c.infants) > 0);
    if (p.model === 'group_tiers' || !(p.categories ?? []).length) return true;
    return (p.categories ?? []).some(
      (c) => c.key === 'infant' || (Number(c.ageMin) <= 1 && Number(c.ageMax ?? 0) >= 1 && c.key !== 'adult'),
    );
  });
}

/** Price of the child band of an option's price plans (MAD), when its plans have one. */
function childBandPrice(o: OptionRecord): number | undefined {
  for (const p of plansOf(o)) {
    const c = (p.categories ?? []).find((x) => x.key === 'child');
    if (c) return (Number(c.priceMinor) || 0) / 100;
  }
  return undefined;
}

const hasPickupDescription = (o: OptionRecord) =>
  !!o.pickup && (!!o.pickup.coverage || !!o.pickup.areaGeoJson || (o.pickup.places ?? []).length > 0);

const COVERAGES = ['place_list', 'radius', 'area', 'on_confirmation'] as const;

/** `resolveOption`: the declared meeting mode, else pickup when a pickup is described. */
function pickupOf(o: OptionRecord): ExperiencePickup | undefined {
  const mode = o.meetingMode || (hasPickupDescription(o) ? 'pickup' : 'meeting_point');
  if (mode !== 'pickup' || !hasPickupDescription(o) || !o.pickup) return undefined;
  const coverage = COVERAGES.find((c) => c === o.pickup?.coverage) ?? 'on_confirmation';
  return {
    coverage,
    places: (o.pickup.places ?? [])
      .filter((p) => p._id && p.name)
      .map((p) => ({
        id: p._id ?? '',
        name: p.name,
        surcharge: (Number(p.surchargeMinor) || 0) / 100,
        instructions: p.instructions || undefined,
      })),
    instructions: o.pickup.instructions || undefined,
    confirmationTiming: o.pickup.confirmationTiming || undefined,
  };
}

const num = (v: number | null | undefined) => (v === null || v === undefined || !Number.isFinite(v) ? undefined : v);

/** Per-participant details `bookingRequirements` checks (ages, heights, weights). */
function requirementsOf(o: OptionRecord): ExperienceRequirements | undefined {
  const r = o.requirements;
  const out: ExperienceRequirements = {
    minAge: num(r?.minAge),
    minHeightCm: num(r?.minHeightCm),
    approvalAboveWeightKg: num(r?.approvalAboveWeightKg),
    adultCompanion: !!r?.requireAdultCompanion || !!o.eligibilityPolicy?.enabled,
  };
  const needed =
    out.adultCompanion ||
    out.minAge !== undefined ||
    out.minHeightCm !== undefined ||
    out.approvalAboveWeightKg !== undefined;
  return needed ? out : undefined;
}

function questionsOf(o: OptionRecord): ExperienceQuestion[] | undefined {
  const list = (o.bookingQuestions ?? [])
    .filter((q) => q.key && q.label?.trim())
    .map((q) => ({
      key: q.key,
      label: q.label.trim(),
      type: (q.type === 'select' || q.type === 'time' ? q.type : 'text') as ExperienceQuestion['type'],
      required: !!q.required,
      choices: (q.choices ?? []).filter((c) => c.trim()),
    }));
  return list.length ? list : undefined;
}

function vehicleAllocationOf(o: OptionRecord): ExperienceVehicleAllocation | undefined {
  const v = o.vehicleAllocation;
  if (!v?.enabled || !(Number(v.peoplePerVehicle) >= 1)) return undefined;
  return {
    peoplePerVehicle: Number(v.peoplePerVehicle),
    maxVehicles: num(v.maxVehicles),
    driverMinAge: num(v.driverMinAge),
    passengerMinAge: num(v.passengerMinAge),
    instructions: v.instructions || undefined,
  };
}

/** One option as a pack, with everything the booking flow asks for it. */
function toPack(a: ActivityRecord, o: OptionRecord, addons: { name: string; price: number }[]): ExperiencePack {
  const own = new Set((o.addons ?? []).filter((x) => !x.required).map((x) => x.name));
  const price = optionPrice(a, o._id);
  return {
    id: o._id,
    label: optionLabel(o) || a.title,
    price: price.amount,
    basis: price.basis,
    groupSize: price.included ?? undefined,
    popular: !!o.isDefault,
    description: o.description?.trim() || undefined,
    bounds: participantBounds(a, o),
    infants: infantsOffered(a, o),
    pickup: pickupOf(o),
    eligibility: o.eligibilityPolicy?.enabled ? { instructions: o.eligibilityPolicy.instructions ?? '' } : undefined,
    questions: questionsOf(o),
    requirements: requirementsOf(o),
    vehicleAllocation: vehicleAllocationOf(o),
    addonIds: addons.map((x, i) => (own.has(x.name) ? addonId(i) : '')).filter(Boolean),
  };
}

function modeLabel(a: ActivityRecord, category: string): string {
  const priv = a.bookingType === 'private';
  const kind =
    a.isMultiDay || (Number(a.durationMinutes) || 0) >= 24 * 60
      ? 'circuit'
      : /transfert/i.test(category)
        ? 'transfer'
        : 'excursion';
  return label(`mode.${kind}.${priv ? 'private' : 'shared'}`);
}

function cancellationLabel(a: ActivityRecord): string {
  const hours = a.cancellationTerms?.fullRefundHoursBefore;
  if (hours != null) return translate(ns.labels, 'cancellation.hours', { hours });
  return label(CANCELLATION[a.cancellationPolicy ?? ''] ?? 'cancellation.provider');
}

function startTimeOf(a: ActivityRecord): string | undefined {
  const times = new Set((a.scheduleTemplate?.weeklyPattern ?? []).flatMap((d) => d.times ?? []));
  return times.size === 1 ? [...times][0] : undefined;
}

const sentenceList = (items: string[] | null | undefined) => (items ?? []).filter(Boolean).join(' · ');

export function toExperience(a: ActivityRecord, baseUrl: string): Experience {
  const summary = toExperienceSummary(a, baseUrl);
  const imgs = sortedImages(baseUrl, a);
  const options = sortedOptions(a);
  const def = defaultOption(a);
  const perGroup = isPerGroup(a);
  const tiers = perGroup ? undefined : expandTiers(def?.priceTiers);
  // New price plans (P6): the child band of the default option's plan; legacy packs: its child price.
  const planChild = def ? childBandPrice(def) : undefined;
  const childPP =
    planChild ?? (def ? Number(def.childPricePerPerson) || 0 : Number(a.childPricePerPerson) || 0);
  const maxFromOptions = Math.max(0, ...options.map((o) => Number(o.maxParticipants) || 0));
  const chargeable = chargeableAddons(a);
  const packs: ExperiencePack[] = options.length
    ? options.map((o) => toPack(a, o, chargeable))
    : [
        {
          // No options: the activity's own price, as the server reads it (none → no price).
          id: 'default',
          label: a.title,
          price: summary.fromPrice,
          basis: summary.priceUnit === 'vehicle' ? 'per_group' : 'per_person',
          popular: true,
          bounds: participantBounds(a, undefined),
          infants: infantsOffered(a, undefined),
        },
      ];
  const addons: ExperienceAddon[] = chargeableAddons(a).map((x, i) => ({
    id: addonId(i),
    label: x.name,
    price: x.price,
  }));
  return {
    ...summary,
    gallery: imgs.slice(1).map((i) => i.url),
    startTime: startTimeOf(a),
    cancellation: cancellationLabel(a),
    mode: modeLabel(a, summary.category),
    place: a.city ? translate(ns.labels, 'place.departure', { city: a.city }) : '',
    description: (a.descriptionLong || a.descriptionShort || '').trim(),
    included: sentenceList(a.includes),
    excluded: sentenceList(a.excludes),
    bring: sentenceList(a.whatToBring),
    meetingPoint: [a.meetingPoint?.address, a.meetingPoint?.instructionsFr].filter(Boolean).join(' '),
    pricing: perGroup ? 'per_group' : tiers ? 'tiered' : 'per_person',
    tiers,
    maxGroup: perGroup && maxFromOptions ? maxFromOptions : Math.max(1, Number(a.groupSizeMax) || 1),
    // A zero child price means «no child rate» for the backend (children pay the adult price):
    // children are then simply counted as participants.
    // On a price plan a child band at 0 means «free» (shown «Gratuit»); on a legacy pack 0 means «no child rate».
    childPrice: perGroup ? null : planChild !== undefined ? planChild : childPP <= 0 ? null : childPP,
    packs,
    defaultPackId: def?._id ?? packs[0]?.id,
    addons,
    languages: a.languages ?? [],
    publicId: a.publicId || undefined,
    waiver: a.waiverRequired
      ? {
          required: true,
          text: a.waiver?.instructions?.trim() || undefined,
          documentUrl: /^https:\/\//i.test(a.waiver?.documentUrl ?? '') ? (a.waiver?.documentUrl ?? undefined) : undefined,
        }
      : undefined,
  };
}

/**
 * Seats still sellable on a departure, null when uncapped: port of
 * `domain/activities/availability/capacityPolicy.js` (effectiveCapacityMax / seatsRemaining).
 */
export function seatsRemaining(slot: SlotRecord): number | null {
  const declared = Number(slot.capacityMax);
  let max: number | null;
  if (Number.isFinite(declared) && declared > 0) max = declared;
  else if (slot.capacityMax === 0) max = 0;
  else if (slot.scheduleId) max = null;
  else {
    const legacy = Number(slot.capacity);
    max = Number.isFinite(legacy) && legacy > 0 ? legacy : null;
  }
  return max === null ? null : Math.max(0, max - (Number(slot.participantsBooked) || 0));
}

/** Days `from` … `from + count − 1` with their open slots. */
export function toExperienceDays(slots: SlotRecord[], from: string, count = 5): ExperienceDay[] {
  const byDay = new Map<string, SlotRecord[]>();
  for (const slot of slots) {
    if (slot.status && slot.status !== 'open') continue;
    if (slot.cutoffAt && new Date(slot.cutoffAt).getTime() <= Date.now()) continue;
    const day = slotDay(slot.date);
    byDay.set(day, [...(byDay.get(day) ?? []), slot]);
  }
  return Array.from({ length: count }, (_, i) => {
    const date = toISODate(addDays(parseISO(from), i));
    const open = (byDay.get(date) ?? []).filter((sl) => seatsRemaining(sl) !== 0);
    const remaining = open.map((sl) => seatsRemaining(sl) ?? Infinity);
    return {
      date,
      available: open.length > 0,
      low: open.length > 0 && Math.max(...remaining) <= 3,
      times: open.map((sl) => sl.startTime ?? '').filter(Boolean),
    };
  });
}

/**
 * The slot a selection books (legacy slot match: day + time + open + option): the open departures of the
 * day for the pack (or any pack) with enough seats for the party, at `time` when given, else the earliest.
 * Two departures at the same time (duplicate rows) → the one with the most seats left.
 */
export function pickSlot(
  slots: SlotRecord[],
  date: string,
  optionId: string | undefined,
  seats = 1,
  time?: string,
): SlotRecord | undefined {
  const room = (sl: SlotRecord) => seatsRemaining(sl) ?? Infinity;
  return slots
    .filter((sl) => (!sl.status || sl.status === 'open') && slotDay(sl.date) === date)
    .filter((sl) => !sl.cutoffAt || new Date(sl.cutoffAt).getTime() > Date.now())
    .filter((sl) => !sl.optionId || !optionId || sl.optionId === optionId)
    .filter((sl) => !time || sl.startTime === time)
    .filter((sl) => room(sl) >= seats)
    .sort((x, y) => (x.startTime ?? '').localeCompare(y.startTime ?? '') || room(y) - room(x))[0];
}

/** Open departures as the booking flow reads them (ACT-6): day, time, option, seats left. */
export function toExperienceSlots(slots: SlotRecord[], now = Date.now()): ExperienceSlot[] {
  return slots
    .filter((sl) => (!sl.status || sl.status === 'open') && !!sl.startTime && !!slotDay(sl.date))
    .filter((sl) => !sl.cutoffAt || new Date(sl.cutoffAt).getTime() > now)
    .map((sl) => ({
      id: sl._id,
      date: slotDay(sl.date),
      time: sl.startTime ?? '',
      endTime: sl.endTime ?? undefined,
      packId: sl.optionId ?? null,
      seatsLeft: seatsRemaining(sl),
    }));
}

/** The pickup of a selection as the backend reads it (`{address, geo, placeId}`). */
export function pickupBody(p: ExperienceSelection['pickup']) {
  if (!p) return undefined;
  const geo = Number.isFinite(p.lat) && Number.isFinite(p.lng) ? { lat: p.lat, lng: p.lng } : undefined;
  return { address: p.address.trim(), geo, placeId: p.placeId || undefined };
}

/** Request body of POST /api/client/activities/:id/quote (and the shared part of the booking). */
export function quoteBody(a: ActivityRecord, sel: ExperienceSelection, slot: SlotRecord | undefined) {
  const addons = chargeableAddons(a);
  const option = sortedOptions(a).find((o) => o._id === sel.packId);
  return {
    optionId: option?._id,
    slotId: slot?._id,
    date: sel.date,
    startTime: slot?.startTime ?? undefined,
    participantCount: { adults: sel.adults, children: sel.children, infants: sel.infants ?? 0 },
    selectedAddons: sel.addonIds
      .map((id) => addons[Number(id.slice(1))])
      .filter((x): x is { name: string; price: number } => !!x)
      .map((x) => ({ name: x.name })),
    pickup: pickupBody(sel.pickup),
    eligibilityGrantId: sel.grantId || undefined,
  };
}

export function toExperienceQuote(q: ActivityQuoteRecord, paymentMode: PaymentMode): ExperienceQuote {
  const t = getT(ns.labels);
  const lines: PriceLine[] = q.breakdown.map((l) => {
    const amount = l.amountMinor / 100;
    const qty = Number(l.quantity) || 0;
    const unit = l.unitMinor != null ? l.unitMinor / 100 : undefined;
    if (l.kind === 'participant') {
      const key = l.categoryKey === 'child' || l.categoryKey === 'infant' ? l.categoryKey : 'adult';
      const name = t(`participants.${key}`);
      const free = Number(l.freeCount) || 0;
      // A verified free entry (eligibility grant): the line says how many are free.
      if (free > 0)
        return {
          kind: 'base' as const,
          label: t('participants.free', { label: name, count: free }),
          amount,
          unitPrice: unit,
          quantityLabel: t(`participants.paying.${key}`, { count: qty - free }),
        };
      return {
        kind: 'base' as const,
        label: name,
        amount,
        unitPrice: unit,
        quantityLabel: key === 'adult' ? String(qty) : t(`participants.count.${key}`, { count: qty }),
      };
    }
    if (l.kind === 'tier') return { kind: 'base' as const, label: t('line.group', { count: qty }), amount };
    // Labels the server sends (add-on names) are shown as they are; ours fill the gaps.
    if (l.kind === 'addon') return { kind: 'fee' as const, label: l.label ?? t('line.option'), amount };
    if (l.kind === 'pickup') return { kind: 'fee' as const, label: t('line.pickup'), amount };
    if (amount < 0) return { kind: 'discount' as const, label: l.label ?? t('line.adjustment'), amount: -amount };
    return {
      kind: 'fee' as const,
      // i18n-ignore-next-line: the server's own label for a seasonal adjustment, compared, not shown
      label: l.label === 'Ajustement saisonnier' || !l.label ? t('line.seasonal') : l.label,
      amount,
    };
  });
  const p = q.pickupSelection;
  return {
    lines,
    total: q.total,
    participants: Number(q.seats) || 0,
    paymentMode,
    bookable: q.bookable !== false,
    blockedBy: q.blockedBy === 'cutoff' || q.blockedBy === 'pickup_required' ? q.blockedBy : null,
    pickupRequired: !!q.pickupRequired,
    pickup: p
      ? {
          covered: !!p.covered,
          needsConfirmation: !!p.requiresConfirmation,
          placeName: p.placeName ?? undefined,
          surcharge: (Number(p.surchargeMinor) || 0) / 100,
          reason: p.reason ?? undefined,
        }
      : undefined,
  };
}

/* ── Free-entry requests (ACT-8/9) and the success page's booking (ACT-10) ── */

export function toEligibility(g: z.infer<typeof EligibilitySchema>): EligibilityRequest {
  const status = g.status === 'approved' || g.status === 'rejected' ? g.status : 'pending';
  return { id: g._id, status, used: !!g.reservation, note: g.reviewNote?.trim() || undefined };
}

/** The voucher QR payload, provider tickets and infants of an experience booking. */
export function bookingExtras(r: z.infer<typeof ActivityReservationExtrasSchema>) {
  const t = r.providerTickets;
  const tickets: ProviderTickets | undefined =
    t && (t.status === 'issued' || t.status === 'pending')
      ? {
          status: t.status,
          documents: (t.documents ?? [])
            .filter((d) => d.url && /^https:\/\//i.test(d.url))
            .map((d) => ({ title: d.title || translate(ns.labels, 'ticket'), url: d.url ?? '' })),
          confirmedStartTime: t.confirmedStartTime || undefined,
          instructions: t.instructions || undefined,
        }
      : undefined;
  const confirmed = r.status === 'confirmed' || r.status === 'completed';
  return {
    qrToken: r.voucher?.qrToken || undefined,
    voucherPdf: confirmed && !!r.voucher?.code,
    tickets,
    infants: Number(r.activityDetails?.participantCount?.infants) || 0,
    experienceId: typeof r.activity === 'string' ? r.activity : (r.activity?._id ?? undefined),
  };
}
